import { apiClient, apiBaseUrl } from '../api/client';

// Simple cache for ETags and blobs
interface CacheEntry {
  etag: string | null;
  blob: Blob;
  timestamp: number;
}

const cache = new Map<string, CacheEntry>();
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

export class PictureApiService {
  static clearCache(urlPattern: string) {
    // Clear cache entries that match the URL pattern
    for (const [key] of cache) {
      if (key.includes(urlPattern)) {
        cache.delete(key);
      }
    }
  }

  static getCachedETag(url: string): string | undefined {
    const entry = cache.get(url);
    if (entry && Date.now() - entry.timestamp < CACHE_DURATION) {
      return entry.etag || undefined;
    }
    return undefined;
  }

  static getCachedBlob(url: string): Blob | null {
    const entry = cache.get(url);
    if (entry && Date.now() - entry.timestamp < CACHE_DURATION) {
      return entry.blob;
    }
    return null;
  }

  static setCachedBlob(url: string, blob: Blob, etag: string | null) {
    cache.set(url, {
      etag,
      blob,
      timestamp: Date.now(),
    });
  }

  static getPictureUrl(
    entityType: 'organization' | 'asset' | 'user',
    orgCode: string,
    entityId: string,
    size?: string,
    documentUid?: string
  ): string {
    const params = new URLSearchParams();
    if (size) params.append('size', size);
    if (documentUid) params.append('documentUid', documentUid);
    const queryString = params.toString() ? `?${params.toString()}` : '';

    let url: string;
    if (entityType === 'organization') {
      url = `/api/v1/organizations/${orgCode}/picture${queryString}`;
    } else if (entityType === 'asset') {
      url = `/api/v1/organizations/${orgCode}/assets/${entityId}/picture${queryString}`;
    } else {
      url = `/api/v1/organizations/${orgCode}/users/${entityId}/picture${queryString}`;
    }

    return `${apiBaseUrl}${url}`;
  }

  static async getPicture(
    entityType: 'organization' | 'asset' | 'user',
    orgCode: string,
    entityId: string,
    size?: string,
    documentUid?: string
  ): Promise<Blob> {
    const params = new URLSearchParams();
    if (size) params.append('size', size);
    if (documentUid) params.append('documentUid', documentUid);
    const queryString = params.toString() ? `?${params.toString()}` : '';

    let url: string;
    if (entityType === 'organization') {
      url = `/api/v1/organizations/${orgCode}/picture${queryString}`;
    } else if (entityType === 'asset') {
      url = `/api/v1/organizations/${orgCode}/assets/${entityId}/picture${queryString}`;
    } else {
      url = `/api/v1/organizations/${orgCode}/users/${entityId}/picture${queryString}`;
    }

    const cachedETag = this.getCachedETag(url);
    const headers: HeadersInit = {};
    if (cachedETag) {
      headers['If-None-Match'] = cachedETag;
    }

    const response = await fetch(`${apiBaseUrl}${url}`, {
      headers,
    });

    if (response.status === 304) {
      const cachedBlob = this.getCachedBlob(url);
      if (cachedBlob) {
        return cachedBlob;
      }
    }

    if (!response.ok) {
      throw new Error(`Failed to load picture: ${response.status}`);
    }

    const blob = await response.blob();
    const etag = response.headers.get('ETag');
    this.setCachedBlob(url, blob, etag);
    return blob;
  }

  static async uploadPicture(
    entityType: 'organization' | 'asset' | 'user',
    orgCode: string,
    entityId: string,
    file: File,
    _onProgress?: (progress: number) => void
  ): Promise<{ document_uid: string }> {
    const formData = new FormData();
    formData.append('file', file);

    let url: string;
    if (entityType === 'organization') {
      url = `/api/v1/organizations/${orgCode}/picture`;
    } else if (entityType === 'asset') {
      url = `/api/v1/organizations/${orgCode}/assets/${entityId}/picture`;
    } else {
      url = `/api/v1/organizations/${orgCode}/users/${entityId}/picture`;
    }

    // Use apiClient's middleware by making a PUT request
    const response = await (apiClient.PUT as any)(url, {
      body: formData,
    });

    if (!response.response.ok) {
      throw new Error(`Upload failed: ${response.response.status}`);
    }

    this.clearCache(url);

    // Return the document_uid from the response
    return response.data as { document_uid: string };
  }

  static async deletePicture(
    entityType: 'organization' | 'asset' | 'user',
    orgCode: string,
    entityId: string
  ): Promise<void> {
    let url: string;
    if (entityType === 'organization') {
      url = `/api/v1/organizations/${orgCode}/picture`;
    } else if (entityType === 'asset') {
      url = `/api/v1/organizations/${orgCode}/assets/${entityId}/picture`;
    } else {
      url = `/api/v1/organizations/${orgCode}/users/${entityId}/picture`;
    }

    const response = await (apiClient.DELETE as any)(url, {});

    if (!response.response.ok) {
      throw new Error(`Failed to delete picture: ${response.response.status}`);
    }

    this.clearCache(url);
  }
}